import { useState } from "react";
import axios from "axios";
import "./create-travel-post.css";

export default function CreateTravelPost({ user, onPostCreated, onClose }) {
  const [step, setStep] = useState(1); // 1: básico, 2: detalhes, 3: mídia, 4: review
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Form data
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    destination: {
      country: "",
      city: "",
      region: "",
      coordinates: { latitude: null, longitude: null },
    },
    startDate: "",
    endDate: "",
    tripType: "solo",
    duration: 0,
    images: [],
    ratings: [],
    overallRating: 0,
    totalBudget: { amount: 0, currency: "USD" },
    expenses: [],
    itinerary: [],
    pointsOfInterest: [],
    tips: [],
    tags: [],
    interests: [],
    transportation: { vehicle: "flight" },
    accommodation: "hotel",
    accommodationName: "",
    accommodationRating: 0,
    privacy: "public",
  });

  const [imageUrls, setImageUrls] = useState([]);
  const [currentTip, setCurrentTip] = useState("");
  const [currentExpense, setCurrentExpense] = useState({ category: "food", amount: 0, currency: "USD" });
  const [currentPOI, setCurrentPOI] = useState({ name: "", category: "landmark" });

  // Triptype options
  const tripTypes = [
    { id: "solo", label: "Solo" },
    { id: "couple", label: "Casal" },
    { id: "family", label: "Família" },
    { id: "friends", label: "Amigos" },
    { id: "business", label: "Negócios" },
    { id: "retreat", label: "Retiro" },
    { id: "adventure", label: "Aventura" },
  ];

  const interests = [
    "adventure",
    "culture",
    "nature",
    "food",
    "shopping",
    "beach",
    "mountains",
    "city",
    "history",
    "nightlife",
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.includes(".")) {
      const keys = name.split(".");
      setFormData((prev) => {
        const newData = { ...prev };
        let obj = newData;
        for (let i = 0; i < keys.length - 1; i++) {
          obj = obj[keys[i]];
        }
        obj[keys[keys.length - 1]] = value;
        return newData;
      });
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      if (newData.startDate && newData.endDate) {
        const start = new Date(newData.startDate);
        const end = new Date(newData.endDate);
        const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
        newData.duration = Math.max(1, days);
      }
      return newData;
    });
  };

  const handleAddImage = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setImageUrls((prev) => [...prev, reader.result]);
        setFormData((prev) => ({
          ...prev,
          images: [
            ...prev.images,
            { url: reader.result, caption: "" },
          ],
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleAddTip = () => {
    if (currentTip.trim()) {
      setFormData((prev) => ({
        ...prev,
        tips: [...prev.tips, { title: currentTip, category: "budget" }],
      }));
      setCurrentTip("");
    }
  };

  const handleAddExpense = () => {
    if (currentExpense.amount > 0) {
      setFormData((prev) => ({
        ...prev,
        expenses: [...prev.expenses, currentExpense],
        totalBudget: {
          ...prev.totalBudget,
          amount: prev.totalBudget.amount + currentExpense.amount,
        },
      }));
      setCurrentExpense({ category: "food", amount: 0, currency: "USD" });
    }
  };

  const handleAddPOI = () => {
    if (currentPOI.name.trim()) {
      setFormData((prev) => ({
        ...prev,
        pointsOfInterest: [...prev.pointsOfInterest, currentPOI],
      }));
      setCurrentPOI({ name: "", category: "landmark" });
    }
  };

  const handleToggleInterest = (interest) => {
    setFormData((prev) => {
      const hasInterest = prev.interests.includes(interest);
      return {
        ...prev,
        interests: hasInterest
          ? prev.interests.filter((i) => i !== interest)
          : [...prev.interests, interest],
      };
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError("");

      // Validações
      if (!formData.title || !formData.destination.country) {
        setError("Título e país são obrigatórios");
        return;
      }

      if (!formData.startDate || !formData.endDate) {
        setError("Datas de início e fim são obrigatórias");
        return;
      }

      const payload = {
        ...formData,
      };

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/travel`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (onPostCreated) {
        onPostCreated(response.data.post);
      }

      setLoading(false);
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao criar post");
      setLoading(false);
    }
  };

  const nextStep = () => {
    if (step === 1) {
      if (!formData.title || !formData.destination.country || !formData.startDate || !formData.endDate) {
        setError("Preencha todos os campos obrigatórios");
        return;
      }
    }
    setStep((prev) => Math.min(prev + 1, 4));
    setError("");
  };

  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  return (
    <div className="create_travel_post_overlay" onClick={onClose}>
      <div className="create_travel_post_modal" onClick={(e) => e.stopPropagation()}>
        <div className="create_travel_header">
          <h2>
            {step === 1 && "Detalhes da Viagem"}
            {step === 2 && "Informações Adicionais"}
            {step === 3 && "Fotos e Mídia"}
            {step === 4 && "Revisar"}
          </h2>
          <button className="close_btn" onClick={onClose}>✕</button>
        </div>

        {error && <div className="error_message">{error}</div>}

        <div className="create_travel_content">
          {/* STEP 1: Básico */}
          {step === 1 && (
            <div className="form_section">
              <div className="form_group">
                <label>Título da Viagem *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Ex: Viagem para Paris em Primavera"
                  maxLength="200"
                />
                <small>{formData.title.length}/200</small>
              </div>

              <div className="form_row">
                <div className="form_group">
                  <label>País *</label>
                  <input
                    type="text"
                    name="destination.country"
                    value={formData.destination.country}
                    onChange={handleInputChange}
                    placeholder="Ex: França"
                  />
                </div>
                <div className="form_group">
                  <label>Cidade</label>
                  <input
                    type="text"
                    name="destination.city"
                    value={formData.destination.city}
                    onChange={handleInputChange}
                    placeholder="Ex: Paris"
                  />
                </div>
              </div>

              <div className="form_row">
                <div className="form_group">
                  <label>Data de Início *</label>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleDateChange}
                  />
                </div>
                <div className="form_group">
                  <label>Data de Fim *</label>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleDateChange}
                  />
                </div>
                {formData.duration > 0 && (
                  <div className="form_group">
                    <label>Duração</label>
                    <input
                      type="text"
                      value={`${formData.duration} dias`}
                      disabled
                    />
                  </div>
                )}
              </div>

              <div className="form_group">
                <label>Tipo de Viagem</label>
                <div className="trip_type_selector">
                  {tripTypes.map((type) => (
                    <button
                      key={type.id}
                      className={`trip_type_btn ${formData.tripType === type.id ? "active" : ""}`}
                      onClick={() =>
                        setFormData((prev) => ({ ...prev, tripType: type.id }))
                      }
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="form_group">
                <label>Descrição</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Conte sobre sua viagem..."
                  maxLength="2000"
                  rows="4"
                />
                <small>{formData.description.length}/2000</small>
              </div>
            </div>
          )}

          {/* STEP 2: Detalhes */}
          {step === 2 && (
            <div className="form_section">
              <div className="form_group">
                <label>Hospedagem</label>
                <select
                  name="accommodation"
                  value={formData.accommodation}
                  onChange={handleInputChange}
                >
                  <option value="hotel">Hotel</option>
                  <option value="airbnb">Airbnb</option>
                  <option value="hostel">Hostel</option>
                  <option value="resort">Resort</option>
                  <option value="villa">Villa</option>
                  <option value="guesthouse">Guest House</option>
                  <option value="campground">Acampamento</option>
                  <option value="other">Outro</option>
                </select>
              </div>

              <div className="form_group">
                <label>Nome da Hospedagem</label>
                <input
                  type="text"
                  name="accommodationName"
                  value={formData.accommodationName}
                  onChange={handleInputChange}
                  placeholder="Ex: Hotel Le Marais"
                />
              </div>

              <div className="form_row">
                <div className="form_group">
                  <label>Avaliação Hospedagem (1-5)</label>
                  <input
                    type="number"
                    name="accommodationRating"
                    min="1"
                    max="5"
                    value={formData.accommodationRating}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form_group">
                  <label>Avaliação Geral (1-5)</label>
                  <input
                    type="number"
                    name="overallRating"
                    min="1"
                    max="5"
                    value={formData.overallRating}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form_group">
                <label>Interesses da Viagem</label>
                <div className="interests_grid">
                  {interests.map((interest) => (
                    <label key={interest} className="interest_checkbox">
                      <input
                        type="checkbox"
                        checked={formData.interests.includes(interest)}
                        onChange={() => handleToggleInterest(interest)}
                      />
                      <span>{interest.charAt(0).toUpperCase() + interest.slice(1)}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form_group">
                <label>Orçamento Total (USD)</label>
                <input
                  type="number"
                  name="totalBudget.amount"
                  value={formData.totalBudget.amount}
                  onChange={handleInputChange}
                  placeholder="0"
                />
              </div>

              <div className="form_section_title">Adicionar Dica</div>
              <div className="add_item_form">
                <input
                  type="text"
                  value={currentTip}
                  onChange={(e) => setCurrentTip(e.target.value)}
                  placeholder="Ex: Compre um passe de transporte...  "
                />
                <button onClick={handleAddTip} className="add_btn">+ Dica</button>
              </div>
              {formData.tips.length > 0 && (
                <div className="items_list">
                  {formData.tips.map((tip, idx) => (
                    <div key={idx} className="list_item">
                      <span>{tip.title}</span>
                      <button
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            tips: prev.tips.filter((_, i) => i !== idx),
                          }))
                        }
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="form_section_title">Adicionar Despesa</div>
              <div className="add_expense_form">
                <select
                  value={currentExpense.category}
                  onChange={(e) =>
                    setCurrentExpense((prev) => ({
                      ...prev,
                      category: e.target.value,
                    }))
                  }
                >
                  <option value="accommodation">Hospedagem</option>
                  <option value="food">Alimentação</option>
                  <option value="transport">Transporte</option>
                  <option value="activities">Atividades</option>
                  <option value="shopping">Compras</option>
                  <option value="other">Outro</option>
                </select>
                <input
                  type="number"
                  value={currentExpense.amount}
                  onChange={(e) =>
                    setCurrentExpense((prev) => ({
                      ...prev,
                      amount: parseFloat(e.target.value) || 0,
                    }))
                  }
                  placeholder="Valor"
                />
                <button onClick={handleAddExpense} className="add_btn">+ Despesa</button>
              </div>
            </div>
          )}

          {/* STEP 3: Mídia */}
          {step === 3 && (
            <div className="form_section">
              <div className="form_group">
                <label>Adicionar Fotos</label>
                <div className="file_input_wrapper">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleAddImage}
                    id="travel_images"
                  />
                  <label htmlFor="travel_images" className="file_label">
                    📸 Clique para adicionar fotos
                  </label>
                </div>
              </div>

              {imageUrls.length > 0 && (
                <div className="images_preview">
                  <h4>Fotos Adicionadas ({imageUrls.length})</h4>
                  <div className="images_grid">
                    {imageUrls.map((url, idx) => (
                      <div key={idx} className="image_preview_item">
                        <img src={url} alt="" />
                        <button
                          onClick={() => {
                            setImageUrls((prev) => prev.filter((_, i) => i !== idx));
                            setFormData((prev) => ({
                              ...prev,
                              images: prev.images.filter((_, i) => i !== idx),
                            }));
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="form_section_title">Adicionar Ponto de Interesse</div>
              <div className="add_poi_form">
                <input
                  type="text"
                  value={currentPOI.name}
                  onChange={(e) =>
                    setCurrentPOI((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Nome do local"
                />
                <select
                  value={currentPOI.category}
                  onChange={(e) =>
                    setCurrentPOI((prev) => ({ ...prev, category: e.target.value }))
                  }
                >
                  <option value="landmark">Marco</option>
                  <option value="restaurant">Restaurante</option>
                  <option value="hotel">Hotel</option>
                  <option value="museum">Museu</option>
                  <option value="park">Parque</option>
                  <option value="beach">Praia</option>
                  <option value="shopping">Shopping</option>
                  <option value="nightlife">Vida Noturna</option>
                  <option value="other">Outro</option>
                </select>
                <button onClick={handleAddPOI} className="add_btn">+ POI</button>
              </div>

              {formData.pointsOfInterest.length > 0 && (
                <div className="items_list">
                  {formData.pointsOfInterest.map((poi, idx) => (
                    <div key={idx} className="list_item">
                      <span>{poi.name} - {poi.category}</span>
                      <button
                        onClick={() =>
                          setFormData((prev) => ({
                            ...prev,
                            pointsOfInterest: prev.pointsOfInterest.filter(
                              (_, i) => i !== idx
                            ),
                          }))
                        }
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* STEP 4: Review */}
          {step === 4 && (
            <div className="form_section review_section">
              <h3>Revisar sua Viagem</h3>
              <div className="review_item">
                <strong>Título:</strong> {formData.title}
              </div>
              <div className="review_item">
                <strong>Destino:</strong> {formData.destination.city}, {formData.destination.country}
              </div>
              <div className="review_item">
                <strong>Período:</strong> {new Date(formData.startDate).toLocaleDateString("pt-BR")} a{" "}
                {new Date(formData.endDate).toLocaleDateString("pt-BR")} ({formData.duration} dias)
              </div>
              <div className="review_item">
                <strong>Tipo:</strong> {tripTypes.find((t) => t.id === formData.tripType)?.label}
              </div>
              <div className="review_item">
                <strong>Fotos:</strong> {imageUrls.length} foto(s)
              </div>
              <div className="review_item">
                <strong>Dicas:</strong> {formData.tips.length}
              </div>
              <div className="review_item">
                <strong>Orçamento:</strong> ${formData.totalBudget.amount}
              </div>
              <div className="review_item">
                <strong>Privacidade:</strong>
                <select
                  name="privacy"
                  value={formData.privacy}
                  onChange={handleInputChange}
                >
                  <option value="public">Público</option>
                  <option value="friends">Apenas Amigos</option>
                  <option value="private">Privado</option>
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="create_travel_footer">
          {step > 1 && (
            <button onClick={prevStep} className="btn_secondary">
              ← Anterior
            </button>
          )}
          {step < 4 && (
            <button onClick={nextStep} className="btn_primary">
              Próximo →
            </button>
          )}
          {step === 4 && (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="btn_primary"
            >
              {loading ? "Publicando..." : "Publicar Viagem"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
